<script setup lang="ts">
import {useData} from "vike-vue/useData";
import ProductDesign from "../../../core/cpn/web/banner/ProductDesign.vue";

const data: any = useData();
</script>

<template>
  <div c2c-0 class="softball-page">
    <section c2c-1 class="hero">
      <p c2c-2 class="hero-kicker">Team Banner Sports</p>
      <h1>Softball Banners</h1>
      <p c2c-3 class="hero-desc">
        Standout softball banners designed for team pride, printed on durable vinyl with fast turnaround.
      </p>
    </section>

    <section c2c-4 class="design-section">
      <div c2c-5 class="section-head">
        <h2>Top Softball Banner Designs</h2>
        <p>Browse popular templates, customize instantly, and order with confidence.</p>
      </div>

      <ul c2c-6 class="design-grid">
        <li v-for="item in data.designs" :key="item._id">
          <ProductDesign :design="item"></ProductDesign>
        </li>
      </ul>

      <div c2c-7 class="more-wrap">
        <a c2c-8 class="link-more" href="/softball-banners/hem-grommets-softball-banners">View more designs</a>
      </div>
    </section>

    <section c2c-9 class="finish-section">
      <h2>Choose Your Finishing Option</h2>
      <p c2c-10 class="finish-intro">
        We offer two popular finishing options for sports banners: Hem &amp; Grommet and Pole Pocket.
      </p>

      <div c2c-11 class="finish-grid">
        <article c2c-12 class="finish-card">
          <a c2c-13 class="finish-media" href="/softball-banners/hem-grommets-softball-banners">
            <img src="/banners/hem-grommet.webp" alt="Hem &amp; Grommets Softball Banners">
          </a>
          <h3>Hem &amp; Grommets Softball Banners</h3>
          <p c2c-14 class="finish-price">
            <span c2c-15 class="sale">$69.99</span>
            <span c2c-16 class="origin">$89.99</span>
          </p>
          <p>UV Printed 13 oz. Matte Vinyl Banner</p>
          <p>Size: 5ft x 3ft (60&quot; x 36&quot;)</p>
          <a c2c-17 class="pick-btn" href="/softball-banners/hem-grommets-softball-banners">Pick Your Design</a>
          <p c2c-18 class="finish-note">
            Reinforced stitched edges and metal grommets make this finishing ideal for outdoor displays and fences.
          </p>
        </article>

        <article c2c-19 class="finish-card">
          <a c2c-20 class="finish-media" href="/softball-banners/pole-pocket-softball-banners">
            <img src="/banners/pole-pocket.webp" alt="Pole Pocket Softball Banners">
          </a>
          <h3>Pole Pocket Softball Banners</h3>
          <p c2c-21 class="finish-price">
            <span c2c-22 class="sale">$69.99</span>
            <span c2c-23 class="origin">$89.99</span>
          </p>
          <p>UV Printed 13 oz. Matte Vinyl Banner</p>
          <p>Size: 5ft x 3ft (60&quot; x 36&quot;)</p>
          <a c2c-24 class="pick-btn" href="/softball-banners/pole-pocket-softball-banners">Pick Your Design</a>
          <p c2c-25 class="finish-note">
            Pole pockets create clean top and bottom channels for rods, giving banners a stretched and polished look.
          </p>
        </article>
      </div>
    </section>

    <section c2c-26 class="content-section">
      <p>
        Team Banner Sports creates custom softball banners that capture team energy for tournaments, home games, and special events.
      </p>

      <p>
        Our banners are produced with high-quality materials and modern printing technology, delivering vivid colors and lasting durability.
      </p>

      <h2>Features</h2>
      <ul c2c-27 class="feature-list">
        <li><strong>Custom Design:</strong> Personalize with your team name, colors, and logo.</li>
        <li><strong>High-Quality Printing:</strong> Crisp visuals with vibrant color output.</li>
        <li><strong>Durable Materials:</strong> Reliable for indoor and outdoor use.</li>
        <li><strong>Easy Ordering:</strong> Fast process from design to delivery.</li>
      </ul>

      <h2>Why Choose Our Softball Banners?</h2>
      <p>
        Softball celebrates teamwork and competitive spirit. Our banners amplify your team identity and make every game day look more professional.
      </p>

      <h2>Get Started Today</h2>
      <p>
        Start designing now or contact
        <a href="mailto:orders@teambannersports.com">orders@teambannersports.com</a>
        for support.
      </p>
    </section>
  </div>
</template>

<style scoped>
.softball-page {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 14px 44px;
  color: #0f172a;
}

.hero {
  border: 1px solid #fed7aa;
  border-radius: 18px;
  padding: 26px 22px;
  background:
    radial-gradient(120% 180% at 100% 0%, rgba(249, 115, 22, 0.16) 0%, rgba(249, 115, 22, 0) 55%),
    linear-gradient(180deg, #fff9f4 0%, #fff2e8 100%);
}

.hero-kicker {
  margin: 0;
  color: #c2410c;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1 {
  margin: 8px 0 0;
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.08;
}

.hero-desc {
  max-width: 760px;
  margin: 12px 0 0;
  color: #334155;
  font-size: 16px;
  line-height: 1.65;
}

.design-section,
.finish-section,
.content-section {
  margin-top: 32px;
}

.section-head h2,
.finish-section h2,
.content-section h2 {
  margin: 0;
  font-size: clamp(22px, 3vw, 30px);
  line-height: 1.2;
}

.section-head p,
.finish-intro {
  margin: 10px 0 0;
  color: #475569;
  font-size: 16px;
}

.design-grid {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.more-wrap {
  margin-top: 18px;
  text-align: center;
}

.link-more {
  display: inline-block;
  padding: 11px 16px;
  border-radius: 10px;
  border: 1px solid #fdba74;
  color: #c2410c;
  font-weight: 700;
  text-decoration: none;
  background: #fff7ed;
}

.link-more:hover {
  border-color: #fb923c;
  background: #ffedd5;
}

.finish-grid {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.finish-card {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}

.finish-media {
  display: block;
}

.finish-media img {
  display: block;
  width: min(100%, 360px);
  margin: 0 auto;
  border-radius: 999px;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
}

.finish-card h3 {
  margin: 14px 0 0;
  text-align: center;
  font-size: 20px;
}

.finish-card p {
  margin: 10px 0 0;
  text-align: center;
  color: #334155;
}

.finish-price .sale {
  color: #dc2626;
  font-size: 28px;
  font-weight: 800;
}

.finish-price .origin {
  margin-left: 8px;
  color: #64748b;
  font-weight: 700;
  text-decoration: line-through;
}

.pick-btn {
  margin: 16px auto 0;
  width: fit-content;
  min-width: 170px;
  display: block;
  padding: 10px 14px;
  border-radius: 10px;
  background: linear-gradient(90deg, #ea580c 0%, #f97316 100%);
  color: #ffffff;
  font-weight: 700;
  text-decoration: none;
}

.pick-btn:hover {
  filter: brightness(1.05);
}

.finish-note {
  color: #64748b;
  font-size: 14px;
  line-height: 1.65;
}

.content-section {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
  background: #ffffff;
}

.content-section p {
  margin: 0;
  color: #334155;
  line-height: 1.75;
}

.content-section p + p,
.content-section h2,
.content-section ul,
.content-section a {
  margin-top: 14px;
}

.feature-list {
  margin: 14px 0 0;
  padding-left: 20px;
}

.feature-list li {
  margin-top: 8px;
  color: #334155;
  line-height: 1.7;
}

.content-section a {
  color: #c2410c;
  font-weight: 600;
}

@media (max-width: 1100px) {
  .design-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .design-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .finish-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .softball-page {
    padding: 16px 10px 36px;
  }

  .hero {
    padding: 18px 14px;
  }

  .design-grid {
    grid-template-columns: 1fr;
  }

  .content-section {
    padding: 16px 14px;
  }
}
</style>
<style lang="scss" scoped></style>